'use client';

import {
  LoaderCircle,
  Network,
  RefreshCw,
  Wallet,
  WalletCards
} from 'lucide-react';
import { StatePanel } from '@repo/ui/components/state-panel';
import {
  useWalletSession,
  type WalletSessionStatus
} from '@/hooks/use-wallet-session';
import { useSignerRecovery } from '@/hooks/use-signer-recovery';
import { track } from '@/lib/analytics';
import { truncateAddress } from '@/lib/format/address';
import { useWalletConnectorsStatus } from '@/providers/wallet-connectors-status';

interface WalletSessionPanelProps {
  /** A session state that is not ready for private reads. */
  status: Exclude<WalletSessionStatus, 'ready'>;
  /** The session has been loading or restoring for too long. */
  timedOut: boolean;
  /** Which gate is showing this panel, for signer recovery analytics. */
  location: 'wallet-gate' | 'overview';
  /** Title of the connect prompt, the one state a page words for itself. */
  connectTitle: string;
  /** Description of the connect prompt. */
  connectDescription: string;
  className?: string;
}

/**
 * The one panel for a wallet session that cannot show private data yet:
 * still checking, waiting on the wallet, signed in with a different account,
 * on a network ROWA does not run on, or not connected at all. Each state
 * says what happened and offers the next step.
 */
export function WalletSessionPanel({
  status,
  timedOut,
  location,
  connectTitle,
  connectDescription,
  className = 'w-full max-w-md'
}: WalletSessionPanelProps) {
  const {
    identityAddress,
    providerAddress,
    connect,
    openWalletManager,
    retryNetworkAvailability
  } = useWalletSession();
  const recovery = useSignerRecovery(location);
  // Dynamic's connect flow has no wallets to offer until its connector
  // chunk arrives, and none at all if it never does.
  const connectorsStatus = useWalletConnectorsStatus();

  if (timedOut && (status === 'loading' || status === 'restoring')) {
    return (
      <StatePanel
        className={className}
        icon={<RefreshCw aria-hidden />}
        tone="warning"
        eyebrow="Wallet session"
        title="Wallet connection is taking longer than expected"
        description="ROWA could not restore the wallet session. Reload to retry without exposing private account data."
        action={{
          label: 'Reload and retry',
          onClick: () => window.location.reload()
        }}
      />
    );
  }

  if (status === 'loading' || status === 'restoring') {
    return (
      <StatePanel
        className={className}
        icon={<LoaderCircle aria-hidden />}
        tone="brand"
        eyebrow="Wallet session"
        title="Checking wallet connection"
        description="Restoring your authenticated wallet before loading private portfolio data."
      />
    );
  }

  if (status === 'connecting') {
    return (
      <StatePanel
        className={className}
        icon={<LoaderCircle aria-hidden />}
        tone="brand"
        eyebrow="Wallet session"
        title="Waiting for wallet approval"
        description="Complete the open request in your wallet. ROWA will continue automatically after approval."
      />
    );
  }

  if (status === 'mismatch') {
    const signedInWallet = identityAddress
      ? truncateAddress(identityAddress)
      : 'your authenticated wallet';
    const browserWallet = providerAddress
      ? truncateAddress(providerAddress)
      : 'no connected account';
    const signerIsChecking = recovery.signerStatus === 'checking';
    const disconnecting = recovery.pending === 'disconnect';

    return (
      <StatePanel
        className={className}
        icon={<WalletCards aria-hidden />}
        tone="warning"
        eyebrow="Account mismatch"
        title="Switch to your ROWA wallet"
        description={
          recovery.failed
            ? `ROWA could not complete that recovery. Open your wallet, select ${signedInWallet}, then try again.`
            : recovery.signerStatus === 'disconnected'
              ? 'Your wallet is no longer connected to ROWA. Reconnect it, or end this session and sign in again.'
              : `ROWA is signed in as ${signedInWallet}, while your browser wallet is using ${browserWallet}. Match them before continuing.`
        }
        action={{
          label:
            recovery.signerStatus === 'disconnected'
              ? 'Reconnect wallet'
              : signerIsChecking
                ? 'Checking wallet'
                : 'I switched accounts, check again',
          onClick: () => void recovery.recover(),
          disabled: signerIsChecking || disconnecting,
          loading: signerIsChecking
        }}
        secondaryAction={{
          label: 'Disconnect and reconnect',
          onClick: () => void recovery.disconnect(),
          disabled: disconnecting,
          loading: disconnecting
        }}
      />
    );
  }

  if (status === 'network-unavailable') {
    return (
      <StatePanel
        className={className}
        icon={<RefreshCw aria-hidden />}
        tone="warning"
        eyebrow="Network service"
        title="Networks are temporarily unavailable"
        description="ROWA could not verify the available networks. Your wallet is still connected; retry before continuing."
        action={{
          label: 'Retry network check',
          onClick: retryNetworkAvailability
        }}
      />
    );
  }

  if (status === 'wrong-network') {
    return (
      <StatePanel
        className={className}
        icon={<Network aria-hidden />}
        tone="warning"
        eyebrow="Network"
        title="Unsupported network"
        description="Use the network control in the navigation bar to choose an available ROWA network. You can also change it in your wallet settings."
        action={{
          label: 'Open wallet settings',
          onClick: openWalletManager
        }}
      />
    );
  }

  if (connectorsStatus === 'failed') {
    return (
      <StatePanel
        className={className}
        icon={<RefreshCw aria-hidden />}
        tone="warning"
        eyebrow="Wallet connection"
        title={connectTitle}
        description="ROWA could not load the list of wallets. Check your connection, then reload the page to connect."
        action={{
          label: 'Reload page',
          onClick: () => window.location.reload()
        }}
      />
    );
  }

  const connectorsLoading = connectorsStatus === 'loading';

  return (
    <StatePanel
      className={className}
      icon={<Wallet aria-hidden />}
      tone="brand"
      eyebrow="Your funds stay in your wallet"
      title={connectTitle}
      description={connectDescription}
      action={{
        label: connectorsLoading ? 'Loading wallets' : 'Connect Wallet',
        disabled: connectorsLoading,
        loading: connectorsLoading,
        onClick: () => {
          track('app.wallet_connect_clicked', { location: 'wallet_gate' });
          connect();
        }
      }}
    />
  );
}
